import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, UserPlus } from 'phosphor-react';
import { toast } from 'sonner';
import { centralApi, IGrupoResumo, IMembroGrupo } from '../../hooks/useCentralPermissoes';
import { AZUL, AZUL_ESCURO, Busca, Cartao, Etiqueta, Eyebrow, TEXTO, TEXTO_SUAVE } from './ui';

const GRUPO_ADMIN = 1;
/** Lista curta: o resto se acha pela busca. */
const VISIVEIS = 10;

const erroDaApi = (e: any, padrao: string) => e?.response?.data?.detail ?? padrao;

/** Quem está no grupo (principal ou adicional) e o botão de adicionar usuário. */
export function GrupoUsuarios({
  grupo,
  grupos,
  onAbrirUsuario,
}: {
  grupo: IGrupoResumo;
  grupos: IGrupoResumo[];
  onAbrirUsuario: (usuarioId: number) => void;
}) {
  const queryClient = useQueryClient();
  const [adicionando, setAdicionando] = useState(false);
  const [digitado, setDigitado] = useState('');
  const [busca, setBusca] = useState('');
  const [buscaMembro, setBuscaMembro] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusca(digitado), 300);
    return () => clearTimeout(t);
  }, [digitado]);

  const { data: membros = [], isLoading } = useQuery({
    queryKey: ['central', 'grupo-membros', grupo.id],
    queryFn: () => centralApi.membrosDoGrupo(grupo.id),
  });
  const { data: candidatos = [], isFetching } = useQuery({
    queryKey: ['central', 'usuarios', busca],
    queryFn: () => centralApi.usuarios(busca),
    enabled: adicionando && busca.trim().length >= 2,
  });

  const nomeGrupo = (id: number) => grupos.find(g => g.id === id)?.nome ?? `Grupo ${id}`;
  const jaEsta = new Set(membros.map(m => m.id));
  const somenteComoPrincipal = grupo.id === GRUPO_ADMIN;

  const atualizar = async () => {
    await queryClient.invalidateQueries({ queryKey: ['central'] });
  };

  const adicionar = useMutation({
    mutationFn: (v: { usuarioId: number; como: 'adicional' | 'principal' }) =>
      centralApi.adicionarMembro(grupo.id, v.usuarioId, v.como),
    onSuccess: async (_, v) => {
      await atualizar();
      toast.success(
        v.como === 'principal'
          ? `${grupo.nome} agora é o grupo principal do usuário`
          : `Usuário adicionado em ${grupo.nome}`,
      );
    },
    onError: e => toast.error(erroDaApi(e, 'Não foi possível adicionar o usuário')),
  });

  const remover = useMutation({
    mutationFn: (usuarioId: number) => centralApi.removerMembro(grupo.id, usuarioId),
    onSuccess: async () => {
      await atualizar();
      toast.success(`Usuário removido de ${grupo.nome}`);
    },
    onError: e => toast.error(erroDaApi(e, 'Não foi possível remover o usuário')),
  });

  const linha = (m: IMembroGrupo) => (
    <Flex
      key={`${m.id}-${m.papel}`}
      align="center"
      gap="12px"
      p="10px 20px"
      borderTop="1px solid rgba(15,23,42,.05)"
      wrap="wrap"
    >
      <Flex
        w="32px"
        h="32px"
        flex="none"
        borderRadius="50%"
        align="center"
        justify="center"
        fontSize="12px"
        fontWeight={700}
        color="#075985"
        bg="#DFF3FF"
      >
        {(m.nome || m.email).slice(0, 2).toUpperCase()}
      </Flex>
      <Box flex="1 1 220px" minW={0}>
        <Text fontSize="13.5px" fontWeight={700} noOfLines={1}>
          {m.nome || m.email}
        </Text>
        <Text fontSize="12px" color={TEXTO_SUAVE} noOfLines={1}>
          {m.email}
        </Text>
      </Box>
      <Etiqueta tom={m.papel === 'principal' ? 'info' : 'sucesso'}>
        {m.papel === 'principal' ? 'Grupo principal' : 'Grupo adicional'}
      </Etiqueta>
      {!m.ativo && <Etiqueta tom="neutro">Inativo</Etiqueta>}
      <Button size="xs" variant="ghost" onClick={() => onAbrirUsuario(m.id)}>
        Abrir usuário
      </Button>
      {m.papel === 'adicional' && (
        <Button
          size="xs"
          variant="ghost"
          color="#991B1B"
          isLoading={remover.isPending && remover.variables === m.id}
          onClick={() => remover.mutate(m.id)}
        >
          Remover
        </Button>
      )}
    </Flex>
  );

  const q = buscaMembro.trim().toLowerCase();
  const encontrados = membros.filter(
    m => !q || `${m.nome ?? ''} ${m.email}`.toLowerCase().includes(q),
  );
  const principais = encontrados.filter(m => m.papel === 'principal');
  const adicionais = encontrados.filter(m => m.papel === 'adicional');
  const totalPrincipais = membros.filter(m => m.papel === 'principal').length;
  const totalAdicionais = membros.length - totalPrincipais;

  const restantes = (lista: IMembroGrupo[]) =>
    lista.length > VISIVEIS && (
      <Text px="20px" py="8px" fontSize="12.5px" color={TEXTO_SUAVE}>
        Mostrando {VISIVEIS} de {lista.length}. Use a busca para achar os outros.
      </Text>
    );

  return (
    <Flex direction="column" gap="16px">
      <Cartao p={0} overflow="hidden">
        <Flex align="center" gap="10px" wrap="wrap" p="16px 20px">
          <Box flex="1 1 240px">
            <Text fontSize="16px" fontWeight={650} color={TEXTO}>
              Usuários do grupo
            </Text>
            <Text fontSize="13px" color={TEXTO_SUAVE}>
              Grupo principal define quem o usuário é; grupo adicional só soma telas, ações,
              permissões gerais e carteira.
            </Text>
          </Box>
          <Button
            size="sm"
            borderRadius="999px"
            leftIcon={<UserPlus size={16} />}
            color="white"
            bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
            _hover={{ bg: AZUL_ESCURO }}
            onClick={() => setAdicionando(v => !v)}
          >
            Adicionar usuário
          </Button>
        </Flex>

        {adicionando && (
          <Box
            p="12px 20px 16px"
            bg="rgba(248,250,252,.7)"
            borderTop="1px solid rgba(15,23,42,.06)"
          >
            <Box maxW="420px">
              <Busca
                valor={digitado}
                onChange={setDigitado}
                placeholder="Buscar colaborador por nome ou e-mail"
              />
            </Box>
            <Flex direction="column" gap="4px" mt="8px">
              {isFetching && <Spinner size="sm" color={AZUL} />}
              {candidatos
                .filter(u => !jaEsta.has(u.id))
                .slice(0, 10)
                .map(u => (
                  <Flex
                    key={u.id}
                    align="center"
                    gap="10px"
                    p="8px 10px"
                    borderRadius="12px"
                    bg="white"
                    wrap="wrap"
                  >
                    <Box flex="1 1 220px" minW={0}>
                      <Text fontSize="13px" fontWeight={600} noOfLines={1}>
                        {u.nome || u.email}
                      </Text>
                      <Text fontSize="12px" color={TEXTO_SUAVE}>
                        Hoje: {nomeGrupo(u.grupo_id)}
                      </Text>
                    </Box>
                    {!somenteComoPrincipal && (
                      <Button
                        size="xs"
                        leftIcon={<Plus size={12} />}
                        variant="outline"
                        isLoading={adicionar.isPending}
                        onClick={() => adicionar.mutate({ usuarioId: u.id, como: 'adicional' })}
                      >
                        Adicionar como grupo adicional
                      </Button>
                    )}
                    <Button
                      size="xs"
                      variant="ghost"
                      isLoading={adicionar.isPending}
                      onClick={() => adicionar.mutate({ usuarioId: u.id, como: 'principal' })}
                    >
                      Tornar o grupo principal
                    </Button>
                  </Flex>
                ))}
              {busca.trim().length >= 2 &&
                !isFetching &&
                candidatos.filter(u => !jaEsta.has(u.id)).length === 0 && (
                  <Text fontSize="12.5px" color={TEXTO_SUAVE}>
                    Ninguém encontrado fora deste grupo.
                  </Text>
                )}
              {somenteComoPrincipal && (
                <Text fontSize="12px" color={TEXTO_SUAVE}>
                  Administrador não pode ser grupo adicional: o usuário passa a ter o Administrador
                  como grupo principal.
                </Text>
              )}
            </Flex>
          </Box>
        )}

        {isLoading ? (
          <Flex justify="center" py={8}>
            <Spinner color={AZUL} />
          </Flex>
        ) : (
          <>
            <Box px="20px" pt="10px">
              {membros.length > VISIVEIS && (
                <Box maxW="360px" mb="10px">
                  <Busca
                    valor={buscaMembro}
                    onChange={setBuscaMembro}
                    placeholder="Buscar no grupo por nome ou e-mail"
                  />
                </Box>
              )}
              <Eyebrow>COMO GRUPO PRINCIPAL · {totalPrincipais}</Eyebrow>
            </Box>
            {principais.slice(0, VISIVEIS).map(linha)}
            {restantes(principais)}
            {q && principais.length === 0 && (
              <Text px="20px" py="10px" fontSize="13px" color={TEXTO_SUAVE}>
                Ninguém com esse nome como grupo principal.
              </Text>
            )}
            <Box px="20px" pt="14px">
              <Eyebrow>COMO GRUPO ADICIONAL · {totalAdicionais}</Eyebrow>
            </Box>
            {adicionais.slice(0, VISIVEIS).map(linha)}
            {restantes(adicionais)}
            {adicionais.length === 0 && (
              <Text px="20px" py="10px" fontSize="13px" color={TEXTO_SUAVE}>
                Ninguém tem este grupo como adicional.
              </Text>
            )}
          </>
        )}
      </Cartao>
    </Flex>
  );
}
